import {useCallback, useEffect, useMemo, useState} from 'react'
import Select, {MultiValue} from 'react-select'
import {
  AlertCircle,
  Beaker,
  CheckCircle2,
  FlaskConical,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react'
import {PageTitle} from '../../../_metronic/layout/core'
import * as API from '../../api'

type DevelopmentUser = {
  id: number
  guid: string
  name: string
  email: string
}

type DevelopmentFeature = {
  id: number
  guid: string
  featureKey: string
  name: string
  description: string
  isInDevelopment: boolean
  userIds: number[]
  createdAt: string | null
  updatedAt: string | null
}

type FeatureDraft = Pick<
  DevelopmentFeature,
  'featureKey' | 'name' | 'description' | 'isInDevelopment' | 'userIds'
>

type UserOption = {
  value: number
  label: string
  email: string
}

const emptyFeature: FeatureDraft = {
  featureKey: '',
  name: '',
  description: '',
  isInDevelopment: true,
  userIds: [],
}

const errorMessages: Record<string, string> = {
  DEVELOPMENT_FEATURE_ALREADY_EXISTS: 'Já existe uma feature com essa chave.',
  DEVELOPMENT_FEATURE_KEY_INVALID: 'Use uma chave em minúsculas, com palavras separadas por hífen.',
  DEVELOPMENT_FEATURE_NAME_REQUIRED: 'Informe o nome da feature.',
  DEVELOPMENT_FEATURE_USERS_INVALID: 'Um ou mais usuários selecionados não estão ativos.',
}

function messageFromError(error: unknown, fallback: string) {
  const responseError = (error as any)?.response?.data?.error
  return errorMessages[responseError] || fallback
}

function normalizeDraft(feature: DevelopmentFeature): FeatureDraft {
  return {
    featureKey: feature.featureKey,
    name: feature.name,
    description: feature.description || '',
    isInDevelopment: feature.isInDevelopment,
    userIds: [...feature.userIds].sort((left, right) => left - right),
  }
}

const DevelopmentFeaturesPage = () => {
  const [features, setFeatures] = useState<DevelopmentFeature[]>([])
  const [drafts, setDrafts] = useState<Record<string, FeatureDraft>>({})
  const [users, setUsers] = useState<DevelopmentUser[]>([])
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [newFeature, setNewFeature] = useState<FeatureDraft>(emptyFeature)
  const [message, setMessage] = useState<{type: 'success' | 'error'; text: string} | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setMessage(null)
    try {
      const response = await API.getDevelopmentFeatures()
      if (!response?.success) throw new Error(response?.error || 'LOAD_ERROR')
      const loadedFeatures: DevelopmentFeature[] = response.data?.features || []
      setFeatures(loadedFeatures)
      setUsers(response.data?.users || [])
      setDrafts(
        Object.fromEntries(
          loadedFeatures.map((feature) => [feature.featureKey, normalizeDraft(feature)])
        )
      )
    } catch (error) {
      setMessage({
        type: 'error',
        text: messageFromError(error, 'Não foi possível carregar as features em desenvolvimento.'),
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const userOptions = useMemo<UserOption[]>(
    () => users.map((user) => ({value: user.id, label: user.name, email: user.email})),
    [users]
  )
  const developmentCount = features.filter((feature) => feature.isInDevelopment).length

  const updateDraft = (featureKey: string, update: Partial<FeatureDraft>) => {
    setDrafts((current) => ({
      ...current,
      [featureKey]: {...current[featureKey], ...update},
    }))
  }

  const isDirty = (feature: DevelopmentFeature) => {
    const draft = drafts[feature.featureKey]
    if (!draft) return false
    return (
      JSON.stringify(normalizeDraft(feature)) !==
      JSON.stringify({
        ...draft,
        userIds: [...draft.userIds].sort((left, right) => left - right),
      })
    )
  }

  const saveFeature = async (feature: DevelopmentFeature) => {
    const draft = drafts[feature.featureKey]
    if (!draft) return
    setSavingKey(feature.featureKey)
    setMessage(null)
    try {
      const response = await API.updateDevelopmentFeature(feature.featureKey, draft)
      if (!response?.success) throw new Error(response?.error || 'SAVE_ERROR')
      await loadData()
      setMessage({type: 'success', text: `Feature “${draft.name}” atualizada.`})
    } catch (error) {
      setMessage({
        type: 'error',
        text: messageFromError(error, 'Não foi possível salvar a feature.'),
      })
    } finally {
      setSavingKey('')
    }
  }

  const createFeature = async () => {
    setSavingKey('__new__')
    setMessage(null)
    try {
      const response = await API.createDevelopmentFeature(newFeature)
      if (!response?.success) throw new Error(response?.error || 'CREATE_ERROR')
      setNewFeature(emptyFeature)
      setShowCreate(false)
      await loadData()
      setMessage({type: 'success', text: 'Feature criada em modo de desenvolvimento.'})
    } catch (error) {
      setMessage({
        type: 'error',
        text: messageFromError(error, 'Não foi possível criar a feature.'),
      })
    } finally {
      setSavingKey('')
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Features em desenvolvimento</PageTitle>
      <div className='w-full space-y-5 px-4 py-5 md:px-6'>
        <section className='overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm'>
          <div className='flex flex-col gap-5 bg-gradient-to-r from-blue-700 via-blue-600 to-sky-500 p-6 text-white md:flex-row md:items-center md:justify-between'>
            <div className='flex items-start gap-4'>
              <span className='rounded-xl bg-white/15 p-3 ring-1 ring-white/25'>
                <FlaskConical className='h-7 w-7 text-white' />
              </span>
              <div>
                <p className='text-xs font-bold uppercase tracking-[0.2em] text-blue-100'>
                  Feature flags
                </p>
                <h1 className='mt-1 text-2xl font-bold text-white'>Controle de funcionalidades em teste</h1>
                <p className='mt-2 max-w-2xl text-sm leading-6 text-blue-50'>
                  Em desenvolvimento, somente os usuários selecionados têm acesso. Fora desse modo,
                  a funcionalidade fica liberada para qualquer usuário que utilize o fluxo.
                </p>
              </div>
            </div>
            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                onClick={() => void loadData()}
                disabled={loading}
                className='inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20 disabled:opacity-50'
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
              <button
                type='button'
                onClick={() => setShowCreate((current) => !current)}
                className='inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50'
              >
                {showCreate ? <X className='h-4 w-4' /> : <Plus className='h-4 w-4' />}
                {showCreate ? 'Fechar' : 'Nova feature'}
              </button>
            </div>
          </div>

          <div className='grid gap-px bg-blue-100 sm:grid-cols-3'>
            <div className='bg-white p-4'>
              <p className='text-xs font-bold uppercase tracking-wide text-blue-600'>Total</p>
              <p className='mt-1 text-2xl font-bold text-blue-950'>{features.length}</p>
            </div>
            <div className='bg-white p-4'>
              <p className='text-xs font-bold uppercase tracking-wide text-amber-600'>
                Em desenvolvimento
              </p>
              <p className='mt-1 text-2xl font-bold text-blue-950'>{developmentCount}</p>
            </div>
            <div className='bg-white p-4'>
              <p className='text-xs font-bold uppercase tracking-wide text-emerald-600'>
                Liberadas
              </p>
              <p className='mt-1 text-2xl font-bold text-blue-950'>
                {features.length - developmentCount}
              </p>
            </div>
          </div>
        </section>

        {message && (
          <div
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium ${
              message.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle2 className='h-5 w-5 shrink-0' />
            ) : (
              <AlertCircle className='h-5 w-5 shrink-0' />
            )}
            {message.text}
          </div>
        )}

        {showCreate && (
          <section className='rounded-2xl border border-blue-200 bg-blue-50/60 p-5 shadow-sm'>
            <div className='mb-4 flex items-center gap-2'>
              <span className='flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm'>
                <Beaker className='h-5 w-5' />
              </span>
              <h2 className='text-lg font-bold text-blue-950'>Cadastrar feature</h2>
            </div>
            <div className='grid gap-4 md:grid-cols-2'>
              <label className='text-sm font-semibold text-slate-700'>
                Nome
                <input
                  value={newFeature.name}
                  onChange={(event) =>
                    setNewFeature((current) => ({...current, name: event.target.value}))
                  }
                  className='mt-1.5 h-11 w-full rounded-lg border border-blue-200 bg-white px-3 font-normal outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                  placeholder='Ex.: Novo cálculo de cashback'
                />
              </label>
              <label className='text-sm font-semibold text-slate-700'>
                Chave técnica
                <input
                  value={newFeature.featureKey}
                  onChange={(event) =>
                    setNewFeature((current) => ({
                      ...current,
                      featureKey: event.target.value.toLowerCase(),
                    }))
                  }
                  className='mt-1.5 h-11 w-full rounded-lg border border-blue-200 bg-white px-3 font-mono text-sm font-normal outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                  placeholder='novo-calculo-cashback'
                />
              </label>
              <label className='text-sm font-semibold text-slate-700 md:col-span-2'>
                Descrição
                <textarea
                  value={newFeature.description}
                  onChange={(event) =>
                    setNewFeature((current) => ({...current, description: event.target.value}))
                  }
                  className='mt-1.5 min-h-[88px] w-full rounded-lg border border-blue-200 bg-white p-3 font-normal outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                  placeholder='Explique o comportamento controlado por esta feature.'
                />
              </label>
            </div>
            <button
              type='button'
              onClick={() => void createFeature()}
              disabled={
                savingKey === '__new__' || !newFeature.name.trim() || !newFeature.featureKey.trim()
              }
              className='mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <Plus className='h-4 w-4' />
              Criar em desenvolvimento
            </button>
          </section>
        )}

        {loading ? (
          <div className='flex min-h-[260px] items-center justify-center rounded-2xl border border-blue-100 bg-white'>
            <RefreshCw className='h-7 w-7 animate-spin text-blue-600' />
          </div>
        ) : features.length === 0 ? (
          <div className='rounded-2xl border border-dashed border-blue-200 bg-blue-50/40 p-12 text-center'>
            <FlaskConical className='mx-auto h-10 w-10 text-blue-400' />
            <p className='mt-3 font-semibold text-blue-900'>Nenhuma feature cadastrada.</p>
          </div>
        ) : (
          <div className='space-y-4'>
            {features.map((feature) => {
              const draft = drafts[feature.featureKey]
              if (!draft) return null
              const selectedOptions = userOptions.filter((option) =>
                draft.userIds.includes(option.value)
              )
              const saving = savingKey === feature.featureKey
              return (
                <section
                  key={feature.featureKey}
                  className='overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm transition-shadow hover:shadow-md'
                >
                  <div className='flex flex-col gap-5 border-b border-blue-100 bg-blue-50/50 p-5 lg:flex-row lg:items-start lg:justify-between'>
                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <h2 className='text-lg font-bold text-blue-950'>{draft.name}</h2>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                            draft.isInDevelopment
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {draft.isInDevelopment ? 'Em desenvolvimento' : 'Liberada para todos'}
                        </span>
                      </div>
                      <code className='mt-2 inline-block rounded bg-blue-100 px-2 py-1 text-xs text-blue-700'>
                        {feature.featureKey}
                      </code>
                      <p className='mt-3 max-w-3xl text-sm leading-6 text-slate-600'>
                        {draft.description || 'Sem descrição.'}
                      </p>
                    </div>

                    <div className='flex items-center justify-between gap-4 rounded-xl border border-blue-200 bg-white p-3 shadow-sm lg:min-w-[300px]'>
                      <div>
                        <p className='text-sm font-bold text-slate-800'>Modo de desenvolvimento</p>
                        <p className='text-xs text-slate-500'>
                          Restringe a feature aos usuários liberados.
                        </p>
                      </div>
                      <button
                        type='button'
                        role='switch'
                        aria-checked={draft.isInDevelopment}
                        onClick={() =>
                          updateDraft(feature.featureKey, {
                            isInDevelopment: !draft.isInDevelopment,
                          })
                        }
                        className={`relative h-7 w-12 shrink-0 rounded-full transition ${
                          draft.isInDevelopment ? 'bg-blue-600' : 'bg-emerald-500'
                        }`}
                      >
                        <span
                          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${
                            draft.isInDevelopment ? 'left-6' : 'left-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className='p-5'>
                    <label className='mb-2 flex items-center gap-2 text-sm font-bold text-slate-700'>
                      <Users className='h-4 w-4 text-blue-600' />
                      Usuários liberados enquanto estiver em desenvolvimento
                    </label>
                    <Select<UserOption, true>
                      isMulti
                      isSearchable
                      closeMenuOnSelect={false}
                      value={selectedOptions}
                      options={userOptions}
                      onChange={(options: MultiValue<UserOption>) =>
                        updateDraft(feature.featureKey, {
                          userIds: options.map((option) => option.value),
                        })
                      }
                      placeholder='Busque por nome ou e-mail'
                      noOptionsMessage={() => 'Nenhum usuário encontrado'}
                      className='text-sm'
                      classNamePrefix='development-feature-user-select'
                      formatOptionLabel={(option) => (
                        <div className='min-w-0'>
                          <div className='truncate font-medium text-slate-800'>{option.label}</div>
                          <div className='truncate text-xs text-slate-400'>{option.email}</div>
                        </div>
                      )}
                      styles={{menu: (base) => ({...base, zIndex: 30})}}
                    />
                    {!draft.isInDevelopment && (
                      <p className='mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700'>
                        <ShieldCheck className='h-4 w-4' />A seleção fica preservada, mas não
                        restringe o uso enquanto a feature estiver liberada.
                      </p>
                    )}
                  </div>

                  <div className='flex justify-end border-t border-blue-50 px-5 pb-5 pt-4'>
                    <button
                      type='button'
                      onClick={() => void saveFeature(feature)}
                      disabled={!isDirty(feature) || saving}
                      className='inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40'
                    >
                      {saving ? (
                        <RefreshCw className='h-4 w-4 animate-spin' />
                      ) : (
                        <Save className='h-4 w-4' />
                      )}
                      Salvar alterações
                    </button>
                  </div>
                </section>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

export default DevelopmentFeaturesPage
